import { getRandomIntInclusive, joinWithAnd, useRouter } from '@application/utils';
import {
  AwarenessBanner,
  Button,
  CardItemBreedSeo,
  CardItemBreeder,
  CardItemLitter,
  ContactInfo,
  Container,
  HeaderProfile,
  IconScaleStars,
  Section,
  SectionCardItemSlider,
  SectionSubtitle,
  SectionText,
  SectionTitle,
  SideLayoutContentWrapper,
  SideLayoutSideWrapper,
  SideLayoutStickyWrapper,
  SideLayoutWrapper,
  Slideshow,
  TranslatableContent,
  TranslateButton,
  WhatsappButton,
} from '@presentation/components';
import { FixedButtonBanner } from '@presentation/components/FixedButtonBanner';
import { useTranslation } from '@presentation/utils';
import { round } from 'lodash/fp';
import type { FC, ReactNode } from 'react';
import { useMemo, useRef } from 'react';
import { TbShare } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import type { BreederPageProps } from '..';
import * as Styled from './styled';

export * from './types';

export const BreederPage: FC<BreederPageProps> = (props) => {
  const {
    header,
    contactInfo,
    content,
    slideshow,
    breeds,
    litters,
    reviewSummary,
    reviewListComponent,
    breedsLink,
    breedNames = [],
    litterItems = [],
    breederItems = [],
    onOpenBuyerChecklist,
    litterItemsLoading = false,
    breederItemsLoading = false,
    moreBreedersLink,
    moreLittersLink,
    showApplyButton,
    onSocialShareClick,
    enableWhatsAppContact,
    onWhatsappClick,
  } = props;

  const { translate } = useTranslation();
  const { routeLocationDescriptor } = useRouter();

  const reviewsRef = useRef<HTMLDivElement>(null);

  const showReviews = useMemo((): boolean => {
    if (reviewSummary?.loading) return true;
    return !!reviewSummary?.amount && reviewSummary?.average != null;
  }, [reviewSummary?.amount, reviewSummary?.average, reviewSummary?.loading]);

  const litterCards = useMemo(() => {
    if (litterItemsLoading) {
      return Array(getRandomIntInclusive(1, 3))
        .fill(undefined)
        .map((_, index) => (
          <CardItemLitter
            style={{ height: 327.5 }}
            key={index}
            loading
            breed=""
            price=""
            location=""
          />
        ));
    }
    return litterItems.map((item, index) => (
      <CardItemLitter style={{ height: 327.5 }} key={index} {...item} />
    ));
  }, [litterItems, litterItemsLoading]);

  const breederCards = useMemo(() => {
    if (breederItemsLoading) {
      return Array(getRandomIntInclusive(1, 3))
        .fill(undefined)
        .map((_, index) => <CardItemBreeder style={{ height: 375 }} key={index} loading />);
    }
    return breederItems.map((item, index) => (
      <CardItemBreeder style={{ height: 375 }} key={index} {...item} />
    ));
  }, [breederItems, breederItemsLoading]);

  const breedNamesJoined = useMemo(
    () => joinWithAnd(translate, breedNames),
    [breedNames, translate],
  );

  const buttons: ReactNode[] = useMemo(() => {
    const btns = [];

    if (enableWhatsAppContact) {
      btns.push(<WhatsappButton outlined={false} onClick={onWhatsappClick} size="medium" />);
    } else if (showApplyButton) {
      btns.push(
        <Button key="apply" color="blue" data-from="breeder" onClick={contactInfo?.onApplyClick}>
          {translate('common.buttons.apply')}
        </Button>,
      );
    }

    if (contactInfo?.showReviewButton) {
      btns.push(
        <Button key="reviews" color="blue" outlined onClick={contactInfo?.onWriteReviewClick}>
          {translate('common.buttons.writeReview')}
        </Button>,
      );
    }
    return btns;
  }, [
    contactInfo?.onApplyClick,
    contactInfo?.onWriteReviewClick,
    contactInfo?.showReviewButton,
    enableWhatsAppContact,
    onWhatsappClick,
    showApplyButton,
    translate,
  ]);

  return (
    <>
      <HeaderProfile {...header}>
        <Button
          shrink
          color="dark"
          iconLeft={<TbShare />}
          size="small"
          onClick={onSocialShareClick}
        >
          {translate('common.buttons.share')}
        </Button>
      </HeaderProfile>
      <Container>
        <SideLayoutWrapper>
          <SideLayoutContentWrapper>
            {!!slideshow && <Slideshow {...slideshow} />}

            {!!content && (
              <Styled.SectionWithMargin topMargin={!slideshow}>
                <SectionTitle>{translate('breederDetails.content.title')}</SectionTitle>
                <Styled.TranslateButtonContainer>
                  <TranslateButton source="breeder" />
                </Styled.TranslateButtonContainer>
                <SectionText
                  dangerouslySetInnerHTML={{
                    __html: translate('breederDetails.content.intro')
                      .replace('$breeds', breedNamesJoined)
                      .replace('$chartHref', routeLocationDescriptor('chart').pathname ?? ''),
                  }}
                />
                <SectionText>
                  <TranslatableContent content={content.description} />
                </SectionText>
                {Object.keys(content)
                  .filter(
                    (contentKey) =>
                      !!content[contentKey as keyof typeof content] && contentKey !== 'description',
                  )
                  .map((contentKey, index) => (
                    <Section key={index}>
                      <SectionSubtitle>
                        {translate(`breederDetails.content.subtitle.${contentKey}`)}
                      </SectionSubtitle>
                      <SectionText>
                        <TranslatableContent
                          content={content[contentKey as keyof typeof content]}
                        />
                      </SectionText>
                    </Section>
                  ))}
              </Styled.SectionWithMargin>
            )}

            <Section>
              <SectionTitle>{translate('breederDetails.litters.title')}</SectionTitle>
              {litters?.length ? (
                <Styled.CardItemContainer>
                  {litters.map((litter, index) => (
                    <Styled.CardItemWrapper key={index}>
                      <CardItemLitter {...litter} />
                    </Styled.CardItemWrapper>
                  ))}
                </Styled.CardItemContainer>
              ) : (
                <SectionText>
                  {translate('breederDetails.litters.emptyText').replaceAll(
                    '$breeds',
                    breedNamesJoined,
                  )}
                </SectionText>
              )}
            </Section>

            {breeds?.length && (
              <Section>
                <SectionTitle>{translate('breederDetails.breeds.title')}</SectionTitle>
                <SectionText>
                  <Link to={breedsLink}>
                    {translate('breederDetails.breeds.cta')}
                    {' >'}
                  </Link>
                </SectionText>
                <Styled.CardItemContainer>
                  {breeds.map((breed, index) => (
                    <Styled.CardItemWrapper key={index}>
                      <CardItemBreedSeo {...breed} />
                    </Styled.CardItemWrapper>
                  ))}
                </Styled.CardItemContainer>
              </Section>
            )}

            {showReviews && (
              <Section>
                <div ref={reviewsRef} style={{ position: 'absolute', top: '-100px', left: '0' }} />

                <SectionTitle>{translate('breederDetails.reviews.title')}</SectionTitle>
                <Section>{reviewListComponent}</Section>
              </Section>
            )}
          </SideLayoutContentWrapper>
          <SideLayoutSideWrapper>
            <SideLayoutStickyWrapper>
              <Styled.SideContentWrapper>
                <Styled.SideContentHeader enableWhatsAppContact={enableWhatsAppContact}>
                  <Styled.SideContentTitle>
                    {translate('breederDetails.contact.card.title')}
                  </Styled.SideContentTitle>
                </Styled.SideContentHeader>
                {!!contactInfo && (
                  <ContactInfo
                    {...contactInfo}
                    values={[
                      ...(contactInfo.values ?? []),
                      ...(!!showReviews && !reviewSummary?.loading
                        ? [
                            {
                              label: '',
                              value: (
                                <Styled.ReviewWrapper>
                                  <IconScaleStars
                                    amount={reviewSummary?.average ?? 0}
                                    onClick={() =>
                                      reviewsRef.current?.scrollIntoView({
                                        behavior: 'smooth',
                                      })
                                    }
                                  />
                                  <Styled.ReviewText>
                                    ({round(reviewSummary?.amount ?? 0)})
                                  </Styled.ReviewText>
                                </Styled.ReviewWrapper>
                              ),
                            },
                          ]
                        : []),
                    ]}
                    buttons={buttons}
                  />
                )}
              </Styled.SideContentWrapper>
            </SideLayoutStickyWrapper>
          </SideLayoutSideWrapper>
        </SideLayoutWrapper>
      </Container>

      <Styled.AwarenessBannerWrapper>
        <AwarenessBanner onClick={onOpenBuyerChecklist} />
      </Styled.AwarenessBannerWrapper>

      <Container>
        <Styled.VerticalMargin>
          <SectionCardItemSlider
            title={translate('common.sections.moreLittersFromBreeds.title').replace(
              '$breeds',
              breedNamesJoined,
            )}
            subtitle={translate('common.sections.moreLittersFromBreeds.text').replace(
              '$breeds',
              breedNamesJoined,
            )}
            loading={litterItemsLoading}
            items={litterCards}
            showMoreLink={moreLittersLink}
          />
        </Styled.VerticalMargin>

        <Styled.VerticalMargin>
          <SectionCardItemSlider
            title={translate('common.sections.moreBreedersFromBreeds.title').replace(
              '$breeds',
              breedNamesJoined,
            )}
            subtitle={translate('common.sections.moreBreedersFromBreeds.text').replace(
              '$breeds',
              breedNamesJoined,
            )}
            loading={breederItemsLoading}
            items={breederCards}
            showMoreLink={moreBreedersLink}
          />
        </Styled.VerticalMargin>
      </Container>

      {showApplyButton && (
        <FixedButtonBanner onClick={contactInfo?.onApplyClick} from="breeder" type="application" />
      )}
      {enableWhatsAppContact && (
        <FixedButtonBanner onClick={onWhatsappClick} from="breeder" type="whatsapp" />
      )}
    </>
  );
};
